import { useTranslation } from "react-i18next";
import { useState, useEffect, Fragment } from "react";
import { UseFormSetValue, UseFormGetValues, UseFormWatch, UseFormRegister } from "react-hook-form";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { GetExtraWorkAgreementQueryResponse, ExtraWorkAgreementTypeRequest } from "api/generatedApi";
import { InputContainer } from "shared/styles/input-container-style";
import { InputFieldCommon, InputFieldProps } from "components/shared/number-input/input-common";
import { formatHoursAndMinutes, formatNumberToPrice } from "utils/formats";
import { ExtraWorkAgreementFormData } from "../hooks/use-input-validation";

const compactSelectSx = { '& .MuiSelect-select': { py: 1, px: 1 } };

interface Props {
  agreement?: GetExtraWorkAgreementQueryResponse;
  setValue: UseFormSetValue<ExtraWorkAgreementFormData>;
  getValue: UseFormGetValues<ExtraWorkAgreementFormData>;
  watch: UseFormWatch<ExtraWorkAgreementFormData>;
  register: UseFormRegister<ExtraWorkAgreementFormData>;
  disabled: boolean;
}

export const EditExtraWorkAgreementTimeOrPayment = (props: Props) => {
  const { setValue, getValue, watch, register, disabled } = props;
  const { t } = useTranslation();
  type ShowElementsType = "Time" | "Payment" | "None";
  const getShowElementType = (type?: ExtraWorkAgreementTypeRequest): ShowElementsType => {
    if (!type) {
      return "None";
    }
    switch (type) {
      case "CompanyHours":
      case "CustomerHours":
        return "Time";
      case "AgreedPayment":
        return "Payment";
      case "Other":
        return "None";
    }
  };

  const extraWorkAgreementType = watch("extraWorkAgreementType");

  const [showElements, setShowElements] = useState<ShowElementsType>(getShowElementType(getValue("extraWorkAgreementType")));
  const [numberValue, setNumberValue] = useState<string>(props.agreement?.paymentDkr?.toString() ?? "");

  const paymentChanges = (value: string) => {
    setNumberValue(value);
    try {
      const val = parseFloat(value.replaceAll(".", "").replace(",", "."));
      setValue("paymentDkr", val);
    } catch {
      setValue("paymentDkr", undefined);
    }
  };

  const paymentInputConfig: InputFieldProps = {
    disableInputField: false,
    inputType: "currency",
    maxValue: 10000000,
    value: numberValue,
    alwaysDisableInputField: false,
    minValue: -10000000,
    onChange: paymentChanges,
  };

  useEffect(() => {
    const newShowElements = getShowElementType(extraWorkAgreementType);
    if (showElements !== newShowElements) {
      setShowElements(newShowElements);
    }
  }, [extraWorkAgreementType]);

  return (
    <InputContainer sx={{ display: "flex", height: disabled ? 1 : 100, minWidth: 175 }}>
      {showElements === "Payment" && (
        <Fragment>
          {disabled ? (
            <Typography color="primary.main" variant="caption">
              {t("captions.paymentView")}
            </Typography>
          ) : (
            <Typography color="primary.main" variant="caption">
              {t("captions.paymentRequired")}
            </Typography>
          )}
          {disabled ? <Typography variant="body1">{formatNumberToPrice(props.agreement?.paymentDkr)}</Typography> : <InputFieldCommon inputFieldProps={paymentInputConfig} />}
        </Fragment>
      )}
      {showElements === "Time" && (
        <Fragment>
          {disabled ? (
            <Typography color="primary.main" variant="caption">
              {t("captions.timeView")}
            </Typography>
          ) : (
            <Typography color="primary.main" variant="caption">
              {t("captions.timeRequired")}
            </Typography>
          )}
          {disabled ? (
            <Typography variant="body1">{formatHoursAndMinutes(props.agreement?.workTime?.hours, props.agreement?.workTime?.minutes)}</Typography>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <FormControl sx={{ my: 1, width: "45%" }}>
                <InputLabel>{t("common.time.hours")}</InputLabel>
                <Select label="hours" defaultValue={props.agreement?.workTime?.hours ?? 0} {...register("workTime.hours")} sx={compactSelectSx}>
                  {Array.from(Array(301).keys()).map((number, index) => {
                    return (
                      <MenuItem key={index} value={number}>
                        {number}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl sx={{ my: 1, width: "45%" }}>
                <InputLabel>{t("common.time.minutes")}</InputLabel>
                <Select label="minutes" defaultValue={props.agreement?.workTime?.minutes ?? 0} {...register("workTime.minutes")} sx={compactSelectSx}>
                  <MenuItem value={0}>0</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={45}>45</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Fragment>
      )}
    </InputContainer>
  );
};
